import { AnyAction, createAsyncThunk, createSlice, ThunkDispatch } from '@reduxjs/toolkit';
import { ITransponderIntermediateResultList, TimeTableEventTabType } from '@App/types';
import { RootState } from '@App/store';

import transponderIntermediateTimesApi from '@App/services/transponderIntermediateTimes/transponderIntermediateTimesApi';
import { setActiveTab } from '@Features/timeTableSelectedEvent/timeTableSelectedEventSlice';
import { setSelectedTimeTableId } from '@Features/timeTable/timeTableSlice';
import { eventsApi } from '@App/services/events/eventsApi';
import { pollingSucceeded, pollingFailed } from '@Features/errorHandler/polling/pollingErrorHandlerSlice';

import { pollingErrorHandlerSlice } from '@Features/errorHandler/polling/pollingErrorHandlerSlice';

export interface IGetTransponderIntermediateTimesPayload {
  timeTableId: number;
}

export interface ITransponderIntermediateResultListState {
  changeTimestamp: number; // Tohle trigeruje mount/unmout pooling kontejneru, tak aby se forsnul reload při znovu kliku (jinak se vysypal state)
  dataLoaded: boolean;
  isFetching: boolean;
  error: any;
  data: ITransponderIntermediateResultList | null;
}

const initialState: ITransponderIntermediateResultListState = {
  changeTimestamp: 0,
  dataLoaded: false,
  isFetching: false,
  error: null,
  data: null,
};

export const getTransponderIntermediateTimesDataAsync = createAsyncThunk(
  'transponderIntermediateTimes/fetchData',
  async ({ timeTableId }: IGetTransponderIntermediateTimesPayload, { dispatch, rejectWithValue, signal, getState }) => {
    try {
      const response = await transponderIntermediateTimesApi.fetchTransponderIntermediateTimesData(timeTableId, signal);

      const thunkDispatch = dispatch as ThunkDispatch<any, any, AnyAction>; // Nutno to přetypovat, TypeScript si s tím nějak
      thunkDispatch(eventsApi.util.updateQueryData('getEventsStates', '', draft => {
        draft.states[timeTableId] = response.eventState;
      }));

      dispatch(pollingSucceeded());

      return response.data;
    } catch (err) {
      dispatch(pollingFailed({ sectionName: 'transponderIntermediateTimes', error: err }));
      return rejectWithValue(err);
    }
  }
);

export const transponderIntermediateTimesSlice = createSlice({
  name: 'transponderIntermediateTimesSlice',
  initialState,
  reducers: {
  },
  extraReducers: (builder) => {
    builder
      .addCase(getTransponderIntermediateTimesDataAsync.pending, state => {
        state.isFetching = true;
      })
      .addCase(getTransponderIntermediateTimesDataAsync.fulfilled, (state, action) => {
        state.isFetching = false;
        state.dataLoaded = true;
        state.error = null;
        state.data = action.payload;
      })
      .addCase(getTransponderIntermediateTimesDataAsync.rejected, (state, action) => {
        if (action.error.name !== 'AbortError') {
          state.isFetching = false;
          state.error = action.payload;
        }
      })
      .addCase(setActiveTab, (state, action) => { // Clear state
        if (action.payload === TimeTableEventTabType.TransponderIntermediateTimes) {
          state.changeTimestamp = Date.now();
        } else {
          Object.assign(state, initialState);
        }
      })
      .addCase(setSelectedTimeTableId, (state, action) => {
        if (action.payload.hasChanged) {
          Object.assign(state, initialState);
        } else {
          state.changeTimestamp = Date.now();
        }
      })
      .addCase(eventsApi.internalActions.unsubscribeQueryResult, state => { // Clear state when movind to different screen
        Object.assign(state, initialState);
      })
      .addCase(pollingErrorHandlerSlice.actions.clearPollingErrors, (state) => {
        state.error = null;
      });
  }
});

export const selectIsFetching = (state: RootState) => state.transponderIntermediateTimes.isFetching;
export const selectError = (state: RootState) => state.transponderIntermediateTimes.error;
export const selectDataLoaded = (state: RootState) => state.transponderIntermediateTimes.dataLoaded;
export const selectData = (state: RootState) => state.transponderIntermediateTimes.data;
export const selectChangeTimestamp = (state: RootState) => state.transponderIntermediateTimes.changeTimestamp;

export default transponderIntermediateTimesSlice.reducer;